import React, { useState, useEffect } from "react";
import { X, Save, Trash2, Plus, ChevronDown, ChevronRight } from "lucide-react";
import {
  generateId,
  MODEL_KEYWORDS,
  WEAPON_KEYWORDS,
  ARMOUR_KEYWORDS,
  EQUIPMENT_KEYWORDS,
  formatCost,
  canAddModel,
  canAddItem,
  getLimitDisplayInfo,
} from "./helpers";
import { KeywordSelector } from "./KeywordSelector";
import { Warband, Model, Item, ModelTemplate, ItemTemplate } from "./types";
import { useForceSaveOnUnmount } from "./hooks/useAutoSave";

interface AddModelModalProps {
  onAdd: (data: { model: Model; newItems: Item[] }) => void;
  onClose: () => void;
  warband: Warband;
  setWarband: (warband: Warband) => void;
  initialMode?: "select-model" | "edit-model-template";
  allModelTemplates: ModelTemplate[];
  updateModelTemplates: (templates: ModelTemplate[]) => void;
  editingTemplate?: ModelTemplate | null;
  models: Model[]; // Add models prop for limit validation
  items: Item[]; // Add items prop for limit validation
  itemTemplates: ItemTemplate[]; // Add item templates for template editing
}

export const AddModelModal = ({
  onAdd,
  onClose,
  warband,
  setWarband,
  initialMode = "select-model",
  editingTemplate,
  allModelTemplates,
  updateModelTemplates,
  models,
  items,
  itemTemplates: propItemTemplates,
}: AddModelModalProps) => {
  // Force save when modal closes
  useForceSaveOnUnmount();

  const [modelTemplates, setModelTemplates] = useState<ModelTemplate[]>(allModelTemplates || []);
  const [itemTemplates, setItemTemplates] = useState<ItemTemplate[]>(propItemTemplates || []);
  const [view, setView] = useState(initialMode);
  const [editingTemplateState, setEditingTemplateState] =
    useState<ModelTemplate | null>(editingTemplate || null);
  const [expandedSections, setExpandedSections] = useState({
    elites: true,
    troopers: true,
  });

  const getKeywordOptions = (category: string) => {
    if (category === "melee" || category === "ranged" || category === "grenade")
      return WEAPON_KEYWORDS;
    if (category === "armour" || category === "shield") return ARMOUR_KEYWORDS;
    if (category === "equipment") return EQUIPMENT_KEYWORDS;
    return [];
  };

  const [templateData, setTemplateData] = useState<ModelTemplate>({
    id: generateId(), // ← FIXED: Always start with a real ID
    templateName: "",
    name: "",
    class: "",
    type: "trooper",
    cost: 0,
    costCurrency: "ducats",
    stats: { movement: 6, melee: 0, ranged: 0, baseArmour: 0, finalArmour: 0 },
    keywords: [],
    abilities: [],
    skills: [],
    itemTemplateIds: [],
    upgrades: [],
    limit: undefined,
  });

  const [newSkill, setNewSkill] = useState({
    name: "",
    table: "",
    description: "",
  });
  const [newAbility, setNewAbility] = useState("");
  const [newAbilityDescription, setNewAbilityDescription] = useState("");
  const [newUpgrade, setNewUpgrade] = useState({
    name: "",
    cost: 0,
    limit: undefined as number | undefined,
    description: "",
    effects: {
      addKeywords: [] as string[],
      modifyStats: {} as { [key: string]: number },
      addAbilities: [] as Array<{ name: string; description: string }>,
      addInbuiltEquipment: [] as any[]
    }
  });


  useEffect(() => {
    if (editingTemplate) {
      setTemplateData(editingTemplate);
      setEditingTemplateState(editingTemplate);
      setView("edit-model-template");
    } else {
      resetTemplateData();
      setEditingTemplateState(null);
    }
  }, [editingTemplate]);

  // Update itemTemplates when prop changes
  useEffect(() => {
    if (propItemTemplates) {
      setItemTemplates(propItemTemplates);
    }
  }, [propItemTemplates]);

  const resetTemplateData = () => {
    setTemplateData({
      id: generateId(), // ← FIXED: Generate new ID when resetting (e.g., creating new)
      templateName: "",
      name: "",
      class: "",
      type: "trooper",
      cost: 0,
      costCurrency: "ducats",
      stats: {
        movement: 6,
        melee: 0,
        ranged: 0,
        baseArmour: 0,
        finalArmour: 0,
      },
      keywords: [],
      abilities: [],
      skills: [],
      itemTemplateIds: [],
      upgrades: [],
      limit: undefined,
    });
  };

  const handleSaveTemplate = () => {
    if (!templateData.templateName.trim()) {
      alert("Please enter a template name");
      return;
    }
    if (!templateData.name || !templateData.class) {
      alert("Please enter model name and class");
      return;
    }

    const template = {
      ...templateData,
      id: templateData.id || generateId(), // ← FINAL SAFETY: never allow blank ID
      templateName: templateData.templateName.trim(),
    };

    let updatedTemplates;
    if (editingTemplateState) {
      updatedTemplates = modelTemplates.map((t) =>
        t.id === editingTemplateState.id ? template : t
      );
    } else {
      updatedTemplates = [...modelTemplates, template];
    }

    updateModelTemplates(updatedTemplates);
    onClose();
    setEditingTemplateState(null);
    resetTemplateData();
    alert(
      `Template "${template.templateName}" ${
        editingTemplateState ? "updated" : "created"
      }!`
    );
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (!confirm("Delete this template?")) return;
    const updatedTemplates = modelTemplates.filter((t) => t.id !== templateId);
    updateModelTemplates(updatedTemplates);
  };

  const handleEditTemplate = (template: ModelTemplate) => {
    setEditingTemplateState(template);
    setTemplateData({ ...template });
    setView("edit-model-template");
  };

  const handleAddFromTemplate = (template: ModelTemplate) => {
    // Check model limits first
    const modelValidation = canAddModel(template, models);
    if (!modelValidation.allowed) {
      alert(modelValidation.reason);
      return;
    }

    // Only charge for the model itself - inbuilt equipment is free and built-in
    let totalDucatCost = template.costCurrency === "ducats" ? template.cost : 0;
    let totalGloryCost = template.costCurrency === "glory" ? template.cost : 0;

    const newItems: Item[] = [];
    const newItemIds: string[] = [];

    // Create items for inbuilt equipment
    if (template.inbuiltEquipment) {
      template.inbuiltEquipment.forEach((equipment) => {
        const newItem = {
          id: generateId(),
          name: equipment.name,
          category: equipment.category,
          keywords: [...equipment.keywords],
          range: equipment.range,
          armourModifier: equipment.armourModifier,
          notes: equipment.notes,
          cost: 0, // Inbuilt equipment is free
          costCurrency: "free" as const,
          isInbuilt: true,
        };

        newItems.push(newItem);
        newItemIds.push(newItem.id);
      });
    }

    if (totalDucatCost > warband.ducats) {
      alert(`Not enough ducats! Need ${totalDucatCost}👑`);
      return;
    }
    if (totalGloryCost > warband.glory) {
      alert(`Not enough glory! Need ${totalGloryCost}☼`);
      return;
    }

    // Create base model with unmodified template stats
    // baseArmour should be the raw template value, not modified by inbuilt equipment
    let newModel = {
      id: generateId(),
      name: template.name,
      class: template.class,
      type: template.type,
      cost: template.cost,
      costCurrency: template.costCurrency,
      stats: { ...template.stats },
      keywords: [...template.keywords],
      abilities: [...template.abilities] as string[] | { name: string; description: string }[],
      skills: template.skills.map((s) => ({ ...s })),
      scars: [],
      xp: 0,
      items: newItemIds,
      alive: true,
      diedOnBattle: undefined,
      templateId: template.id,
      enabledUpgrades: [], // Start with no upgrades enabled
    };

    onAdd({ model: newModel, newItems });

    setWarband({
      ...warband,
      ducats: warband.ducats - totalDucatCost,
      glory: warband.glory - totalGloryCost,
    });

    onClose();
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const addAbility = () => {
    if (newAbility.trim()) {
      setTemplateData({
        ...templateData,
        abilities: [
          ...templateData.abilities,
          {
            name: newAbility.trim(),
            description: newAbilityDescription.trim(),
          },
        ] as string[] | { name: string; description: string }[],
      });
      setNewAbility("");
      setNewAbilityDescription("");
    }
  };

  const addSkill = () => {
    if (newSkill.name.trim() && newSkill.table.trim()) {
      setTemplateData({
        ...templateData,
        skills: [...templateData.skills, { ...newSkill }],
      });
      setNewSkill({ name: "", table: "", description: "" });
    } else {
      alert("Please enter skill name and table");
    }
  };




  const addUpgrade = () => {
    if (!newUpgrade.name.trim()) return;

    const upgrade = {
      id: generateId(),
      name: newUpgrade.name.trim(),
      cost: newUpgrade.cost || 0,
      limit: undefined,
      effects: {
        addKeywords: [],
        modifyStats: {},
        addAbilities: [],
        addItems: [],
        addInbuiltEquipment: []
      }
    };

    setTemplateData({
      ...templateData,
      upgrades: [...(templateData.upgrades || []), upgrade],
    });

    setNewUpgrade({
      name: "",
      cost: 0,
      limit: undefined,
      description: "",
      effects: {
        addKeywords: [],
        modifyStats: {},
        addAbilities: [],
        addInbuiltEquipment: []
      }
    });
  };

  const updateUpgrade = (index: number, upgrade: any) => {
    const updatedUpgrades = [...(templateData.upgrades || [])];
    updatedUpgrades[index] = upgrade;
    setTemplateData({
      ...templateData,
      upgrades: updatedUpgrades,
    });
  };

  const removeUpgrade = (index: number) => {
    const updatedUpgrades = (templateData.upgrades || []).filter((_, i) => i !== index);
    setTemplateData({
      ...templateData,
      upgrades: updatedUpgrades,
    });
  };

  const eliteTemplates = modelTemplates.filter((t) => t.type === "elite");
  const trooperTemplates = modelTemplates.filter((t) => t.type === "trooper");

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-3">
      <div className="ledger-shell p-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto text-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-semibold uppercase tracking-wider">
            {view === "select-model"
              ? "Add Model from Template"
              : "Create/Edit Model Template"}
          </h2>
          <button onClick={onClose} className="text-[#E6E5D3] hover:text-white">
            <X size={24} />
          </button>
        </div>

        {view === "select-model" && (
          <>
            {initialMode !== "select-model" && (
              <div className="mb-4">
                <button
                  onClick={() => {
                    resetTemplateData();
                    setView("edit-model-template");
                  }}
                  className="w-full ledger-btn px-3 py-2 flex items-center justify-center gap-2 font-semibold uppercase tracking-wider text-xs"
                >
                  <Plus size={20} /> Create New Model Template
                </button>
              </div>
            )}

            {modelTemplates.length === 0 ? (
              <div className="ledger-panel p-6 text-center">
                <p className="text-[#111] mb-3 font-semibold">
                  No model templates created yet.
                </p>
                <p className="text-[#777] text-xs">
                  Create templates to quickly add models to your warband!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="ledger-panel">
                  <button
                    onClick={() => toggleSection("elites")}
                    className="w-full flex items-center justify-between p-3"
                  >
                    <h3 className="text-sm font-semibold flex items-center gap-2 text-[#111] uppercase tracking-wider">
                      {expandedSections.elites ? (
                        <ChevronDown size={20} />
                      ) : (
                        <ChevronRight size={20} />
                      )}
                      ELITES ({eliteTemplates.length})
                    </h3>
                  </button>
                  {expandedSections.elites && (
                    <div className="p-4 pt-0 space-y-3">
                      {eliteTemplates.map((template) => (
                        <TemplateCard
                          key={template.id}
                          template={template}
                          itemTemplates={itemTemplates}
                          onAdd={handleAddFromTemplate}
                          onEdit={handleEditTemplate}
                          models={models}
                          warband={warband}
                          onDelete={handleDeleteTemplate}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-gray-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection("troopers")}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-650"
                  >
                    <h3 className="text-xl font-bold text-gray-300 flex items-center gap-2">
                      {expandedSections.troopers ? (
                        <ChevronDown size={20} />
                      ) : (
                        <ChevronRight size={20} />
                      )}
                      TROOPERS ({trooperTemplates.length})
                    </h3>
                  </button>
                  {expandedSections.troopers && (
                    <div className="p-4 pt-0 space-y-3">
                      {trooperTemplates.map((template) => (
                        <TemplateCard
                          key={template.id}
                          template={template}
                          itemTemplates={itemTemplates}
                          onAdd={handleAddFromTemplate}
                          onEdit={handleEditTemplate}
                          models={models}
                          warband={warband}
                          onDelete={handleDeleteTemplate}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {view === "edit-model-template" && (
          <>
            <div className="space-y-4 max-h-[calc(90vh-200px)] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateData.templateName}
                  onChange={(e) =>
                    setTemplateData({
                      ...templateData,
                      templateName: e.target.value,
                    })
                  }
                  placeholder="e.g., 'Knight Standard'"
                  className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Model Name
                  </label>
                  <input
                    type="text"
                    value={templateData.name}
                    onChange={(e) =>
                      setTemplateData({ ...templateData, name: e.target.value })
                    }
                    className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Class
                  </label>
                  <input
                    type="text"
                    value={templateData.class}
                    onChange={(e) =>
                      setTemplateData({
                        ...templateData,
                        class: e.target.value,
                      })
                    }
                    className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Type
                  </label>
                  <select
                    value={templateData.type}
                    onChange={(e) =>
                      setTemplateData({ ...templateData, type: e.target.value as "elite" | "trooper" })
                    }
                    className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                  >
                    <option value="trooper">Trooper</option>
                    <option value="elite">Elite</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Cost
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={templateData.cost}
                      onChange={(e) =>
                        setTemplateData({
                          ...templateData,
                          cost: parseInt(e.target.value) || 0,
                        })
                      }
                      className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                    />
                    <select
                      value={templateData.costCurrency}
                      onChange={(e) =>
                        setTemplateData({
                          ...templateData,
                          costCurrency: e.target.value as "ducats" | "glory",
                        })
                      }
                      className="bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                    >
                      <option value="ducats">Ducats 👑</option>
                      <option value="glory">Glory ☼</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Limit (optional)
                  </label>
                  <input
                    type="number"
                    value={templateData.limit || ""}
                    onChange={(e) =>
                      setTemplateData({
                        ...templateData,
                        limit: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Max number of models..."
                    className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {(["movement", "melee", "ranged", "baseArmour"] as const).map((stat) => (
                  <div key={stat}>
                    <label className="block text-xs text-gray-400 mb-1">
                      {stat === "baseArmour"
                        ? "Base Armour"
                        : stat.charAt(0).toUpperCase() + stat.slice(1)}
                    </label>
                    <input
                      type="number"
                      value={templateData.stats[stat]}
                      onChange={(e) =>
                        setTemplateData({
                          ...templateData,
                          stats: {
                            ...templateData.stats,
                            [stat]: parseInt(e.target.value) || 0,
                            ...(stat === "baseArmour"
                              ? { finalArmour: parseInt(e.target.value) || 0 }
                              : {}),
                          },
                        })
                      }
                      className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-red-500 focus:outline-none text-center"
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Keywords
                </label>
                <KeywordSelector
                  selected={templateData.keywords}
                  onChange={(kw) =>
                    setTemplateData({ ...templateData, keywords: kw })
                  }
                  availableKeywords={MODEL_KEYWORDS}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Abilities
                </label>
                <div className="space-y-2">
                  {templateData.abilities.map((ability, idx) => (
                    <div key={idx} className="bg-gray-600 px-3 py-2 rounded">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-white">
                          {typeof ability === "string"
                            ? ability
                            : ability.name || "Unnamed Ability"}
                        </span>
                        <button
                          onClick={() =>
                            setTemplateData({
                              ...templateData,
                              abilities: (templateData.abilities as any[]).filter(
                                (_, i) => i !== idx
                              ) as string[] | { name: string; description: string }[],
                            })
                          }
                          className="text-red-400 hover:text-red-300"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <textarea
                        value={
                          typeof ability === "string"
                            ? ""
                            : ability.description || ""
                        }
                        onChange={(e) => {
                          const updatedAbilities = [...templateData.abilities] as any[];
                          updatedAbilities[idx] = {
                            ...(updatedAbilities[idx] as any),
                            description: e.target.value,
                          };
                          setTemplateData({
                            ...templateData,
                            abilities: updatedAbilities as string[] | { name: string; description: string }[],
                          });
                        }}
                        placeholder="Description..."
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none text-sm"
                        rows={2}
                      />
                    </div>
                  ))}
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newAbility}
                      onChange={(e) => setNewAbility(e.target.value)}
                      placeholder="Ability name..."
                      className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                    />
                    <textarea
                      value={newAbilityDescription}
                      onChange={(e) => setNewAbilityDescription(e.target.value)}
                      placeholder="Description..."
                      className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                      rows={2}
                    />
                    <button
                      onClick={addAbility}
                      className="w-full bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800"
                    >
                      Add Ability
                    </button>
                  </div>
                </div>
              </div>

              {templateData.type === "elite" && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Skills
                  </label>
                  <div className="space-y-2">
                    {templateData.skills.map((skill, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-gray-600 px-3 py-2 rounded"
                      >
                        <span className="flex-1 text-white">
                          <span className="font-semibold">{skill.name}</span>
                          <span className="text-gray-400 text-sm ml-2">
                            ({skill.table})
                          </span>
                        </span>
                        <button
                          onClick={() =>
                            setTemplateData({
                              ...templateData,
                              skills: templateData.skills.filter(
                                (_, i) => i !== idx
                              ),
                            })
                          }
                          className="text-red-400 hover:text-red-300"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={newSkill.name}
                        onChange={(e) =>
                          setNewSkill({ ...newSkill, name: e.target.value })
                        }
                        placeholder="Skill name..."
                        className="bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                      />
                      <input
                        type="text"
                        value={newSkill.table}
                        onChange={(e) =>
                          setNewSkill({ ...newSkill, table: e.target.value })
                        }
                        placeholder="Table (e.g., Melee)"
                        className="bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                      />
                    </div>
                    <input
                      type="text"
                      value={newSkill.description}
                      onChange={(e) =>
                        setNewSkill({
                          ...newSkill,
                          description: e.target.value,
                        })
                      }
                      placeholder="Description..."
                      className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
                    />
                    <button
                      onClick={addSkill}
                      className="w-full bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800"
                    >
                      Add Skill
                    </button>
                  </div>
                </div>
              )}

              {/* Upgrades Section */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Upgrades
                  </label>
                </div>

                {/* Simple Add Upgrade Form */}
                <div className="bg-gray-700 rounded p-3 border border-gray-600 mb-4">
                  <div className="mb-3">
                    <input
                      type="text"
                      value={newUpgrade.name}
                      onChange={(e) => setNewUpgrade({ ...newUpgrade, name: e.target.value })}
                      placeholder="Upgrade name..."
                      className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm mb-2"
                    />
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <input
                        type="number"
                        value={newUpgrade.cost}
                        onChange={(e) => setNewUpgrade({ ...newUpgrade, cost: parseInt(e.target.value) || 0 })}
                        placeholder="Cost..."
                        className="bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                      />
                      <input
                        type="number"
                        value={newUpgrade.limit || ""}
                        onChange={(e) => setNewUpgrade({ ...newUpgrade, limit: e.target.value ? parseInt(e.target.value) : undefined })}
                        placeholder="Limit (optional)..."
                        className="bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                      />
                    </div>
                    <textarea
                      value={newUpgrade.description || ""}
                      onChange={(e) => setNewUpgrade({ ...newUpgrade, description: e.target.value })}
                      placeholder="Upgrade description (optional)..."
                      rows={2}
                      className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                    />
                  </div>
                  <button
                    onClick={addUpgrade}
                    className="w-full bg-yellow-900 text-white px-3 py-2 rounded hover:bg-yellow-800 text-sm"
                  >
                    Add Upgrade
                  </button>
                </div>

                {/* Display existing upgrades */}
                {templateData.upgrades && templateData.upgrades.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {templateData.upgrades.map((upgrade, idx) => (
                      <div key={upgrade.id} className="bg-gray-700 rounded p-3 border border-gray-600">
                        <div className="flex justify-between items-start mb-2">
                          <input
                            type="text"
                            value={upgrade.name}
                            onChange={(e) => updateUpgrade(idx, { ...upgrade, name: e.target.value })}
                            placeholder="Upgrade name..."
                            className="flex-1 bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm mr-2"
                          />
                          <button
                            onClick={() => removeUpgrade(idx)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X size={16} />
                          </button>
                        </div>

                        <div className="mb-2">
                          <input
                            type="text"
                            value={upgrade.name}
                            onChange={(e) => updateUpgrade(idx, { ...upgrade, name: e.target.value })}
                            placeholder="Upgrade name..."
                            className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm mb-2"
                          />
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <input
                              type="number"
                              value={upgrade.cost || 0}
                              onChange={(e) => updateUpgrade(idx, { ...upgrade, cost: parseInt(e.target.value) || 0 })}
                              placeholder="Cost (ducats)"
                              className="bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                            />
                            <input
                              type="number"
                              value={upgrade.limit || ""}
                              onChange={(e) => updateUpgrade(idx, { ...upgrade, limit: e.target.value ? parseInt(e.target.value) : undefined })}
                              placeholder="Limit (optional)"
                              className="bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                            />
                          </div>
                          <textarea
                            value={upgrade.description || ""}
                            onChange={(e) => updateUpgrade(idx, { ...upgrade, description: e.target.value })}
                            placeholder="Upgrade description (optional)..."
                            rows={2}
                            className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm mb-2"
                          />
                        </div>

                        {/* Add Keywords */}
                        <div className="mt-3">
                          <label className="block text-xs text-gray-400 mb-1">Add Keywords</label>
                          <KeywordSelector
                            selected={upgrade.effects?.addKeywords || []}
                            onChange={(keywords) => {
                              updateUpgrade(idx, {
                                ...upgrade,
                                effects: { ...upgrade.effects, addKeywords: keywords }
                              });
                            }}
                            availableKeywords={MODEL_KEYWORDS}
                          />
                        </div>

                        {/* Modify Stats */}
                        <div className="mt-3">
                          <label className="block text-xs text-gray-400 mb-1">Stat Modifications</label>
                          <div className="grid grid-cols-2 gap-2">
                            {(['movement', 'melee', 'ranged', 'baseArmour'] as const).map((stat) => (
                              <div key={stat}>
                                <label className="block text-xs text-gray-500 mb-1">{stat}</label>
                                <input
                                  type="number"
                                  value={(upgrade.effects?.modifyStats || {})[stat] || 0}
                                  onChange={(e) => {
                                    const modifyStats = { ...(upgrade.effects?.modifyStats || {}) };
                                    modifyStats[stat] = parseInt(e.target.value) || 0;
                                    updateUpgrade(idx, {
                                      ...upgrade,
                                      effects: { ...upgrade.effects, modifyStats }
                                    });
                                  }}
                                  className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm text-center"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Add Abilities */}
                        <div className="mt-3">
                          <label className="block text-xs text-gray-400 mb-1">Add Abilities</label>
                          <div className="space-y-2">
                            {(upgrade.effects?.addAbilities || []).map((ability, abilityIdx) => (
                              <div key={abilityIdx} className="border border-gray-500 rounded p-2">
                                <div className="flex gap-2 mb-2">
                                  <input
                                    type="text"
                                    value={ability.name}
                                    onChange={(e) => {
                                      const addAbilities = [...(upgrade.effects?.addAbilities || [])];
                                      addAbilities[abilityIdx] = { ...ability, name: e.target.value };
                                      updateUpgrade(idx, {
                                        ...upgrade,
                                        effects: { ...upgrade.effects, addAbilities }
                                      });
                                    }}
                                    placeholder="Ability name..."
                                    className="flex-1 bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                                  />
                                  <button
                                    onClick={() => {
                                      const addAbilities = (upgrade.effects?.addAbilities || []).filter((_, i) => i !== abilityIdx);
                                      updateUpgrade(idx, {
                                        ...upgrade,
                                        effects: { ...upgrade.effects, addAbilities }
                                      });
                                    }}
                                    className="text-red-400 hover:text-red-300 p-1"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                                <textarea
                                  value={ability.description}
                                  onChange={(e) => {
                                    const addAbilities = [...(upgrade.effects?.addAbilities || [])];
                                    addAbilities[abilityIdx] = { ...ability, description: e.target.value };
                                    updateUpgrade(idx, {
                                      ...upgrade,
                                      effects: { ...upgrade.effects, addAbilities }
                                    });
                                  }}
                                  placeholder="Ability description..."
                                  rows={2}
                                  className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                                />
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                const addAbilities = [...(upgrade.effects?.addAbilities || []), { name: '', description: '' }];
                                updateUpgrade(idx, {
                                  ...upgrade,
                                  effects: { ...upgrade.effects, addAbilities }
                                });
                              }}
                              className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                            >
                              <Plus size={14} /> Add Ability
                            </button>
                          </div>
                        </div>

                        {/* Add Items */}
                        <div className="mt-3">
                          <label className="block text-xs text-gray-400 mb-1">Add Items</label>
                          <div className="space-y-2">
                            {(upgrade.effects?.addItems || []).map((itemId, itemIdx) => (
                              <div key={itemIdx} className="flex gap-2 items-center">
                                <select
                                  value={itemId}
                                  onChange={(e) => {
                                    const addItems = [...(upgrade.effects?.addItems || [])];
                                    addItems[itemIdx] = e.target.value;
                                    updateUpgrade(idx, {
                                      ...upgrade,
                                      effects: { ...upgrade.effects, addItems }
                                    });
                                  }}
                                  className="flex-1 bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                                >
                                  <option value="">Select Item...</option>
                                  {itemTemplates.map((template) => (
                                    <option key={template.id} value={template.id}>
                                      {template.name} ({formatCost(template.cost, template.costCurrency)})
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => {
                                    const addItems = (upgrade.effects?.addItems || []).filter((_, i) => i !== itemIdx);
                                    updateUpgrade(idx, {
                                      ...upgrade,
                                      effects: { ...upgrade.effects, addItems }
                                    });
                                  }}
                                  className="text-red-400 hover:text-red-300 p-1"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                const addItems = [...(upgrade.effects?.addItems || []), ''];
                                updateUpgrade(idx, {
                                  ...upgrade,
                                  effects: { ...upgrade.effects, addItems }
                                });
                              }}
                              className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                            >
                              <Plus size={14} /> Add Item
                            </button>
                          </div>
                        </div>

                        {/* Add Inbuilt Equipment */}
                        <div className="mt-3">
                          <label className="block text-xs text-gray-400 mb-1">Add Inbuilt Equipment</label>
                          <div className="space-y-2">
                            {(upgrade.effects?.addInbuiltEquipment || []).map((equipment, equipIdx) => (
                              <div key={equipIdx} className="border border-gray-500 rounded p-2">
                                <div className="flex gap-2 mb-2">
                                  <input
                                    type="text"
                                    value={equipment.name}
                                    onChange={(e) => {
                                      const addInbuiltEquipment = [...(upgrade.effects?.addInbuiltEquipment || [])];
                                      addInbuiltEquipment[equipIdx] = { ...equipment, name: e.target.value };
                                      updateUpgrade(idx, {
                                        ...upgrade,
                                        effects: { ...upgrade.effects, addInbuiltEquipment }
                                      });
                                    }}
                                    placeholder="Equipment name..."
                                    className="flex-1 bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                                  />
                                  <button
                                    onClick={() => {
                                      const addInbuiltEquipment = (upgrade.effects?.addInbuiltEquipment || []).filter((_, i) => i !== equipIdx);
                                      updateUpgrade(idx, {
                                        ...upgrade,
                                        effects: { ...upgrade.effects, addInbuiltEquipment }
                                      });
                                    }}
                                    className="text-red-400 hover:text-red-300 p-1"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                  <select
                                    value={equipment.category}
                                    onChange={(e) => {
                                      const addInbuiltEquipment = [...(upgrade.effects?.addInbuiltEquipment || [])];
                                      addInbuiltEquipment[equipIdx] = { ...equipment, category: e.target.value };
                                      updateUpgrade(idx, {
                                        ...upgrade,
                                        effects: { ...upgrade.effects, addInbuiltEquipment }
                                      });
                                    }}
                                    className="bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                                  >
                                    <option value="melee">Melee</option>
                                    <option value="ranged">Ranged</option>
                                    <option value="armour">Armour</option>
                                    <option value="shield">Shield</option>
                                    <option value="equipment">Equipment</option>
                                    <option value="grenade">Grenade</option>
                                  </select>
                                  <input
                                    type="text"
                                    value={equipment.armourModifier || ""}
                                    onChange={(e) => {
                                      const addInbuiltEquipment = [...(upgrade.effects?.addInbuiltEquipment || [])];
                                      addInbuiltEquipment[equipIdx] = { ...equipment, armourModifier: e.target.value || undefined };
                                      updateUpgrade(idx, {
                                        ...upgrade,
                                        effects: { ...upgrade.effects, addInbuiltEquipment }
                                      });
                                    }}
                                    placeholder="Armour modifier"
                                    className="bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                                  />
                                </div>
                                <textarea
                                  value={equipment.notes || ""}
                                  onChange={(e) => {
                                    const addInbuiltEquipment = [...(upgrade.effects?.addInbuiltEquipment || [])];
                                    addInbuiltEquipment[equipIdx] = { ...equipment, notes: e.target.value || undefined };
                                    updateUpgrade(idx, {
                                      ...upgrade,
                                      effects: { ...upgrade.effects, addInbuiltEquipment }
                                    });
                                  }}
                                  placeholder="Equipment notes..."
                                  rows={2}
                                  className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                                />
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                const addInbuiltEquipment = [...(upgrade.effects?.addInbuiltEquipment || []), {
                                  id: generateId(),
                                  name: "New Equipment",
                                  category: "melee",
                                  keywords: [],
                                  range: undefined,
                                  armourModifier: undefined,
                                  notes: undefined
                                }];
                                updateUpgrade(idx, {
                                  ...upgrade,
                                  effects: { ...upgrade.effects, addInbuiltEquipment }
                                });
                              }}
                              className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                            >
                              <Plus size={14} /> Add Equipment
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {(!templateData.upgrades || templateData.upgrades.length === 0) && (
                  <div className="text-gray-400 text-sm italic mb-4">
                    No upgrades added yet
                  </div>
                )}

              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Inbuilt Equipment
                  </label>
                  <button
                    onClick={() => {
                      const newEquipment = {
                        id: generateId(),
                        name: "New Inbuilt Equipment",
                        category: "melee",
                        keywords: [],
                        range: undefined,
                        armourModifier: undefined,
                        notes: undefined,
                      };
                      setTemplateData({
                        ...templateData,
                        inbuiltEquipment: [...(templateData.inbuiltEquipment || []), newEquipment],
                      });
                    }}
                    className="bg-yellow-900 text-white px-3 py-1 rounded hover:bg-yellow-800 text-sm flex items-center gap-1"
                  >
                    <Plus size={16} /> Add Inbuilt Equipment
                  </button>
                </div>

                {!templateData.inbuiltEquipment || templateData.inbuiltEquipment.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">
                    No inbuilt equipment
                  </p>
                ) : (
                  <div className="space-y-2">
                    {templateData.inbuiltEquipment.map((equipment, index) => (
                      <div
                        key={equipment.id}
                        className="bg-gray-700 p-3 rounded border border-gray-600"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <input
                            type="text"
                            value={equipment.name}
                            onChange={(e) => {
                              const updatedEquipment = [...(templateData.inbuiltEquipment || [])];
                              updatedEquipment[index] = { ...equipment, name: e.target.value };
                              setTemplateData({
                                ...templateData,
                                inbuiltEquipment: updatedEquipment,
                              });
                            }}
                            className="flex-1 bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm mr-2"
                            placeholder="Equipment name..."
                          />
                          <button
                            onClick={() => {
                              const updatedEquipment = (templateData.inbuiltEquipment || []).filter((_, i) => i !== index);
                              setTemplateData({
                                ...templateData,
                                inbuiltEquipment: updatedEquipment,
                              });
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X size={16} />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <select
                            value={equipment.category}
                            onChange={(e) => {
                              const updatedEquipment = [...(templateData.inbuiltEquipment || [])];
                              updatedEquipment[index] = { ...equipment, category: e.target.value };
                              setTemplateData({
                                ...templateData,
                                inbuiltEquipment: updatedEquipment,
                              });
                            }}
                            className="bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                          >
                            <option value="melee">Melee</option>
                            <option value="ranged">Ranged</option>
                            <option value="armour">Armour</option>
                            <option value="shield">Shield</option>
                            <option value="equipment">Equipment</option>
                            <option value="grenade">Grenade</option>
                          </select>

                          <input
                            type="text"
                            value={equipment.armourModifier || ""}
                            onChange={(e) => {
                              const updatedEquipment = [...(templateData.inbuiltEquipment || [])];
                              updatedEquipment[index] = { ...equipment, armourModifier: e.target.value || undefined };
                              setTemplateData({
                                ...templateData,
                                inbuiltEquipment: updatedEquipment,
                              });
                            }}
                            placeholder="Armour modifier (e.g., -1 INJURY MODIFIER)"
                            className="bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                          />
                        </div>

                        {(equipment.category === "ranged" || equipment.category === "grenade") && (
                          <input
                            type="text"
                            value={equipment.range || ""}
                            onChange={(e) => {
                              const updatedEquipment = [...(templateData.inbuiltEquipment || [])];
                              updatedEquipment[index] = { ...equipment, range: e.target.value || undefined };
                              setTemplateData({
                                ...templateData,
                                inbuiltEquipment: updatedEquipment,
                              });
                            }}
                            placeholder='Range (e.g., 12"/Melee)'
                            className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm mb-2"
                          />
                        )}

                        <textarea
                          value={equipment.notes || ""}
                          onChange={(e) => {
                            const updatedEquipment = [...(templateData.inbuiltEquipment || [])];
                            updatedEquipment[index] = { ...equipment, notes: e.target.value || undefined };
                            setTemplateData({
                              ...templateData,
                              inbuiltEquipment: updatedEquipment,
                            });
                          }}
                          placeholder="Special rules, notes..."
                          rows={2}
                          className="w-full bg-gray-600 text-white px-2 py-1 rounded border border-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
                        />

                        <div className="mt-2">
                          <label className="block text-xs text-gray-400 mb-1">
                            Keywords
                          </label>
                          <KeywordSelector
                            selected={equipment.keywords || []}
                            onChange={(keywords) => {
                              const updatedEquipment = [...(templateData.inbuiltEquipment || [])];
                              updatedEquipment[index] = { ...equipment, keywords };
                              setTemplateData({
                                ...templateData,
                                inbuiltEquipment: updatedEquipment,
                              });
                            }}
                            availableKeywords={getKeywordOptions(equipment.category)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={handleSaveTemplate}
                className="flex-1 bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800"
              >
                <Save size={16} className="inline mr-2" />
                {editingTemplateState ? "Update Template" : "Save Template"}
              </button>
              <button
                onClick={() => {
                  setView("select-model");
                  setEditingTemplateState(null);
                  resetTemplateData();
                }}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

// TemplateCard
const TemplateCard = ({ template, itemTemplates, onAdd, onEdit, onDelete, models, warband }: {
  template: ModelTemplate;
  itemTemplates: ItemTemplate[];
  onAdd: (template: ModelTemplate) => void;
  onEdit: (template: ModelTemplate) => void;
  onDelete: (id: string) => void;
  models: Model[];
  warband: Warband;
}) => {
  const templateItems = itemTemplates.filter((it) =>
    template.itemTemplateIds?.includes(it.id)
  );

  let totalDucatCost = template.costCurrency === "ducats" ? template.cost : 0;
  let totalGloryCost = template.costCurrency === "glory" ? template.cost : 0;
  templateItems.forEach((item) => {
    if (item.costCurrency === "ducats") totalDucatCost += item.cost;
    if (item.costCurrency === "glory") totalGloryCost += item.cost;
  });

  const limitInfo = getLimitDisplayInfo(template, warband, itemTemplates, models);

  return (
    <div className="bg-[#1a1a1a] text-white rounded-none p-4 border border-[#333] shadow-sm">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <h4 className="text-xl font-bold text-white">
            {template.templateName}
          </h4>
          <div className="text-gray-300">
            {template.name} • {template.class}
          </div>
          <div className="flex gap-4 mt-2 text-sm text-gray-400">
            <span>Mv: {template.stats.movement}"</span>
            <span>Me: {template.stats.melee}D</span>
            <span>Rn: {template.stats.ranged}D</span>
            <span>Ar: {template.stats.baseArmour}</span>
          </div>
          <div className="mt-2 flex gap-2">
            {totalDucatCost > 0 && (
              <span className="text-yellow-400 font-semibold">
                {totalDucatCost}👑
              </span>
            )}
            {totalGloryCost > 0 && (
              <span className="text-blue-400 font-semibold">
                {totalGloryCost}☼
              </span>
            )}
            {limitInfo.limit && (
              <span className={`font-semibold ${limitInfo.isAtLimit ? 'text-red-400' : 'text-gray-400'}`}>
                {limitInfo.current}/{limitInfo.limit}
              </span>
            )}
          </div>
          {template.keywords.length > 0 && (
            <div className="mt-2 text-xs text-gray-400">
              {template.keywords.join(", ")}
            </div>
          )}
          {template.skills.length > 0 && (
            <div className="mt-2 text-xs text-green-400">
              Skills: {template.skills.map((s) => s.name).join(", ")}
            </div>
          )}
          {templateItems.length > 0 && (
            <div className="mt-2 text-xs text-blue-400">
              Equipment: {templateItems.map((i) => i.name).join(", ")}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => onAdd(template)}
          disabled={limitInfo.isAtLimit}
          className={`flex-1 px-4 py-2 rounded-none font-semibold ${
            limitInfo.isAtLimit
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-[#2d2d2d] text-white border border-[#444] hover:bg-[#3a3a3a]'
          }`}
        >
          {limitInfo.isAtLimit ? 'At Limit' : 'Add to Warband'}
        </button>
        <button
          onClick={() => onEdit(template)}
          className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-600"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(template.id)}
          className="bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};
